/**
 * Analytics utility to dynamically load and initialize analytics tools
 * Converted to TypeScript for safer, production-grade usage across the app.
 *
 * Key goals:
 * - Lazy-load heavy analytics libraries only when needed to reduce bundle size
 * - Provide resilient fallbacks (no-ops) to avoid runtime crashes
 * - Offer typed, self-documenting APIs for use throughout the codebase
 */

// ============================
// Global window augmentations
// ============================
declare global {
  interface Window {
    // Customer.io analytics SDK (created by snippet)
    cioanalytics?: {
      track?: (eventName: string, properties?: Record<string, unknown>) => void;
      identify?: (userId: string, traits?: Record<string, unknown>) => void;
      page?: () => void;
      reset?: () => void;
      [key: string]: unknown;
    };
    // Microsoft Clarity SDK
    clarity?: (
      action: 'identify' | string,
      id?: string,
      props?: Record<string, unknown>
    ) => void;
    // Optional bootstrap function if present to initialize clarity
    initClarity?: (userData?: Record<string, unknown>) => void;
    // Meta Pixel fbq
    fbq?: (
      command: 'init' | 'track' | 'trackCustom' | string,
      ...args: unknown[]
    ) => void;
    // Production flag set in index.html
    isProduction?: boolean;
  }
}

// ============================
// Types and interfaces
// ============================
export interface AnalyticsInitOptions {
  mixpanelToken?: string;
  userData?: {
    id?: string;
    email?: string;
    name?: string;
    [key: string]: unknown;
  } | null;
  debug?: boolean;
  mixpanelOptions?: Record<string, unknown>;
}

export interface NoopAnalytics {
  track: (eventName: string, properties?: Record<string, unknown>) => void;
  identify: (userId: string) => void;
  people: {
    set: (traits?: Record<string, unknown>) => void;
    increment: (prop: string, by?: number) => void;
    [key: string]: unknown;
  };
  init: (...args: unknown[]) => void;
  reset: () => void;
  load: (...args: unknown[]) => void;
  page: () => void;
  user: () => void;
  [key: string]: unknown;
}

export type Mixpanel = NoopAnalytics & {
  identify?: (userId: string) => void;
};

// ============================
// Module state (singletons)
// ============================
let mixpanelInstance: Mixpanel | null = null;
let clarityInitialized = false;
let customerIOInitialized = false;
let isInitializing = false;
let initPromise: Promise<{
  mixpanel: Mixpanel;
  customerIO: unknown;
  clarityInitialized: boolean;
}> | null = null;

// ============================
// Default configuration
// ============================
const DEFAULT_CONFIG: Required<Pick<AnalyticsInitOptions, 'debug'>> & {
  mixpanelToken?: string;
  autoInit: boolean;
} = {
  mixpanelToken: import.meta.env.VITE_MIXPANEL_PROJECT_TOKEN,
  autoInit: true,
  debug: false,
};

// ============================
// Internals: No-op factory
// ============================
/**
 * Create a no-op implementation to prevent errors when analytics fails to load
 * Always safe to call; used as a resilient fallback.
 */
const createNoopAnalytics = (): NoopAnalytics => {
  const noop = () => {
    // Intentionally empty – avoids runtime crashes when analytics is unavailable
  };
  return {
    track: noop,
    identify: noop,
    people: {
      set: noop,
      increment: noop,
    },
    init: noop,
    reset: noop,
    load: noop,
    page: noop,
    user: noop,
  };
};

// ============================
// Public: Mixpanel
// ============================
/**
 * Lazy load and initialize Mixpanel.
 * Ensures that the heavy SDK is only downloaded when needed.
 *
 * @param token - Mixpanel API token
 * @param options - Initialization options
 * @returns The Mixpanel instance or a no-op fallback
 */
export const initMixpanel = async (
  token?: string,
  options: Record<string, unknown> = {}
): Promise<Mixpanel> => {
  if (mixpanelInstance) return mixpanelInstance;

  try {
    // Dynamically import mixpanel only when needed
    const mixpanel = await import('mixpanel-browser');

    const defaultOptions = {
      debug: false,
      track_pageview: true,
      persistence: 'localStorage',
      autocapture: false, // Disable autocapture to reduce payload size
    } as const;

    const mergedOptions = { ...defaultOptions, ...options } as Record<string, unknown>;

    // mixpanel.default exists in ESM import of mixpanel-browser
    (mixpanel.default as unknown as Mixpanel).init?.(
      token || DEFAULT_CONFIG.mixpanelToken,
      mergedOptions
    );

    mixpanelInstance = mixpanel.default as unknown as Mixpanel;
    return mixpanelInstance;
  } catch (error) {
     
    console.error('Failed to initialize Mixpanel:', error);
    // Return a dummy implementation to prevent errors when called
    mixpanelInstance = createNoopAnalytics();
    return mixpanelInstance;
  }
};

// ============================
// Public: Microsoft Clarity
// ============================
/**
 * Initialize Microsoft Clarity if available on window.
 *
 * @param userData - Optional user data for identification
 * @returns Success status
 */
export const initClarity = async (
  userData: AnalyticsInitOptions['userData'] = null
): Promise<boolean> => {
  if (clarityInitialized) return true;

  if (typeof window !== 'undefined' && typeof window.clarity === 'function') {
    try {
      if (userData && userData.id) {
        window.clarity?.('identify', userData.id, {
          email: userData.email,
          name: userData.name,
        });
      }
      clarityInitialized = true;
      return true;
    } catch (error) {
       
      console.error('Failed to initialize Clarity:', error);
      return false;
    }
  } else if (
    typeof window !== 'undefined' &&
    typeof window.initClarity === 'function'
  ) {
    try {
      window.initClarity?.(userData || {});
      clarityInitialized = true;
      return true;
    } catch (error) {
       
      console.error('Failed to initialize Clarity:', error);
      return false;
    }
  }

  return false;
};

// ============================
// Public: Customer.io
// ============================
/**
 * Initialize Customer.io from the preloaded snippet if present.
 *
 * @returns The Customer.io instance or a no-op fallback
 */
export const initCustomerIO = async (): Promise<unknown> => {
  if (customerIOInitialized && typeof window !== 'undefined' && window.cioanalytics) {
    return window.cioanalytics;
  }

  try {
    if (typeof window !== 'undefined' && window.cioanalytics) {
      // The snippet already creates window.cioanalytics
      customerIOInitialized = true;

      if (typeof window.cioanalytics.page === 'function') {
        window.cioanalytics.page();
      }

      return window.cioanalytics;
    }
    return createNoopAnalytics();
  } catch (error) {
     
    console.error('Failed to initialize Customer.io:', error);
    return createNoopAnalytics();
  }
};

// ============================
// Public: Event tracking
// ============================
/**
 * Track an event across all available analytics platforms.
 * Safely degrades in non-production or when services are unavailable.
 *
 * @param eventName - Name of the event to track
 * @param properties - Event properties
 */
export const trackEvent = async (
  eventName: string,
  properties: Record<string, unknown> = {}
): Promise<void> => {
  const APP_STATE = (import.meta.env.VITE_APP_STATE as string) || 'development';
  if (APP_STATE !== 'production') {
     
    console.log(`Tracking event: ${eventName}`, properties);
    return;
  }

  if (!eventName) {
     
    console.warn('trackEvent called without an event name');
    return;
  }

  try {
    if (!mixpanelInstance) {
      await ensureInitialized();
    }

    // Mixpanel tracking (with enhanced safeguards)
    if (mixpanelInstance) {
      try {
        if (typeof mixpanelInstance.track === 'function') {
          mixpanelInstance.track(eventName, properties);
        }
      } catch (mpError) {
         
        console.error('Error tracking event with Mixpanel:', mpError);
      }
    }

    // Customer.io tracking (with enhanced safeguards)
    if (typeof window !== 'undefined' && window.cioanalytics) {
      try {
        if (typeof window.cioanalytics.track === 'function') {
          setTimeout(() => {
            window.cioanalytics?.track?.(eventName, properties);
          }, 100);
        }
      } catch (cioError) {
         
        console.error('Error tracking event with Customer.io:', cioError);
      }
    }

    // Meta Pixel custom event tracking
    if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
      try {
        if (properties && Object.keys(properties).length > 0) {
          window.fbq('trackCustom', eventName, properties);
        } else {
          window.fbq('trackCustom', eventName);
        }
      } catch (fbqError) {
         
        console.error('Error tracking event with Meta Pixel:', fbqError);
      }
    }
  } catch (error) {
     
    console.error('Error tracking event:', error);
  }
};

// ============================
// Public: Identify users (Mixpanel)
// ============================
/**
 * Identify a user in Mixpanel.
 *
 * @param userId - User ID
 * @param traits - User traits/properties for Mixpanel
 */
export const identifyUserMixpanel = async (
  userId: string,
  traits: Record<string, unknown> = {}
): Promise<void> => {
  if (!userId) {
     
    console.warn('identifyUserMixpanel called with invalid userId');
    return;
  }

  try {
    if (!mixpanelInstance) {
      await ensureInitialized();
    }

    if (mixpanelInstance) {
      try {
        if (typeof mixpanelInstance.identify === 'function') {
          mixpanelInstance.identify(userId);
        }

        if (
          (mixpanelInstance as NoopAnalytics).people &&
          typeof (mixpanelInstance as NoopAnalytics).people.set === 'function'
        ) {
          // Ensure load_prop presence if some SDK versions rely on it
          const peopleObj = (mixpanelInstance as NoopAnalytics).people as Record<string, unknown> & {
            __loaded?: boolean;
            load_prop?: () => void;
          };

          if (peopleObj && peopleObj.__loaded !== false && peopleObj.load_prop === undefined) {
            peopleObj.load_prop = () => {};
          }

          (mixpanelInstance as NoopAnalytics).people.set(traits);
        }
      } catch (mpError) {
         
        console.error('Error in Mixpanel identification:', mpError);
      }
    }
  } catch (error) {
     
    console.error('Error identifying user in Mixpanel:', error);
  }
};

// ============================
// Public: Identify users (Customer.io)
// ============================
/**
 * Identify a user in Customer.io.
 *
 * @param userId - User ID
 * @param traits - User traits/properties for Customer.io
 */
export const identifyUserCustomerIO = async (
  userId: string,
  traits: Record<string, unknown> = {}
): Promise<void> => {
  if (!userId) {
     
    console.warn('identifyUserCustomerIO called with invalid userId');
    return;
  }

  try {
    await initCustomerIO();

    if (typeof window !== 'undefined' && window.cioanalytics) {
      try {
        if (typeof window.cioanalytics.identify === 'function') {
          window.cioanalytics.identify(userId, traits);
        }
      } catch (cioError) {
         
        console.error('Error in Customer.io identification:', cioError);
      }
    }
  } catch (error) {
     
    console.error('Error identifying user in Customer.io:', error);
  }
};

// ============================
// Public: Ensure initialization
// ============================
/**
 * Ensures all analytics systems are initialized.
 * Uses singleton pattern to prevent multiple simultaneous initializations.
 */
export const ensureInitialized = async (
  options: AnalyticsInitOptions = {}
): Promise<{
  mixpanel: Mixpanel;
  customerIO: unknown;
  clarityInitialized: boolean;
}> => {
  if (mixpanelInstance) return { mixpanel: mixpanelInstance, customerIO: window.cioanalytics, clarityInitialized };

  if (isInitializing && initPromise) {
    return initPromise;
  }

  isInitializing = true;
  initPromise = initAllAnalytics(options);

  try {
    const result = await initPromise;
    return result;
  } catch (error) {
     
    console.error('Failed to initialize analytics:', error);
    return {
      mixpanel: createNoopAnalytics(),
      customerIO: createNoopAnalytics(),
      clarityInitialized: false,
    };
  } finally {
    isInitializing = false;
  }
};

// ============================
// Public: Initialize all
// ============================
/**
 * Initialize all analytics tools at once.
 *
 * @param options - Configuration options
 * @returns Object containing all initialized analytics instances
 */
export const initAllAnalytics = async (
  options: AnalyticsInitOptions = {}
): Promise<{
  mixpanel: Mixpanel;
  customerIO: unknown;
  clarityInitialized: boolean;
}> => {
  const { mixpanelToken = DEFAULT_CONFIG.mixpanelToken, userData = null, debug = DEFAULT_CONFIG.debug } = options;

  try {
    const mixpanel = await initMixpanel(mixpanelToken, {
      debug,
      ...(options.mixpanelOptions || {}),
    });

    const customerIO = await initCustomerIO();
    const clarity = await initClarity(userData);

    return {
      mixpanel,
      customerIO,
      clarityInitialized: clarity,
    };
  } catch (error) {
     
    console.error('Analytics initialization error:', error);
    return {
      mixpanel: createNoopAnalytics(),
      customerIO: createNoopAnalytics(),
      clarityInitialized: false,
    };
  }
};


// ============================
// Default export for convenience
// ============================
export default {
  initMixpanel,
  initClarity,
  initCustomerIO,
  trackEvent,
  identifyUserMixpanel,
  identifyUserCustomerIO,
  initAllAnalytics,
  ensureInitialized,
};


